import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Container, Row, Col, Card, Spinner, Form, InputGroup, Badge } from 'react-bootstrap';
import { Link, useNavigate, useNavigationType, useSearchParams } from 'react-router-dom';
import { userService } from '../services/apiService';
import { useAuthToken } from '../hooks/useAuthToken';
import StackedTable from '../components/StackedTable';
import { LoadingState, ErrorState, serverMessage } from '../components/PageState';
import { roleLabel, EMPTY } from '../utils/format';
import { rowClick } from '../utils/rows';

// The server returns at most this many matches
const RESULT_LIMIT = 100;
const MIN_QUERY_LENGTH = 2;

// Company admins get a plain "Admin" badge and the narrower admin roles say which they are; everyone else gets nothing
const roleBadge = (role) => {
  if (!role || !role.endsWith('_ADMIN')) return null;
  return <Badge bg="primary">{role === 'COMPANY_ADMIN' ? 'Admin' : roleLabel(role)}</Badge>;
};

const UserSearch = () => {
  const navigate = useNavigate();
  const ensureToken = useAuthToken();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  // The box keeps its own state so typing stays responsive; ?q= follows it, so Back returns to the same search.
  // A search handed over from the home page arrives as ?q=
  const [searchParams, setSearchParams] = useSearchParams();
  const [searchTerm, setSearchTerm] = useState(() => searchParams.get('q') || '');
  // The query the results on screen belong to
  const [resultsFor, setResultsFor] = useState(null);
  // Only the newest request may update the results, so a slow older reply never replaces newer ones
  const requestId = useRef(0);
  const firstSearch = useRef(true);

  // Follow ?q= when it changes from outside the box, as when the Users link is followed from a search
  // or Back is pressed. The box's own writes replace the entry, and arrive a moment after the keystroke.
  const urlQuery = searchParams.get('q') || '';
  const navigationType = useNavigationType();
  useEffect(() => {
    if (navigationType !== 'REPLACE') setSearchTerm(urlQuery);
  }, [urlQuery, navigationType]);

  const query = searchTerm.trim();
  const longEnough = query.length >= MIN_QUERY_LENGTH;

  const searchUsers = useCallback(async (term) => {
    const id = ++requestId.current;
    try {
      setLoading(true);
      setError(null);
      await ensureToken();
      const response = await userService.searchUsers(term);
      if (id !== requestId.current) return;
      setUsers(response.data);
      setResultsFor(term);
    } catch (err) {
      if (id !== requestId.current) return;
      console.error('Failed to search users:', err);
      setError(serverMessage(err, 'Could not search users.'));
    } finally {
      if (id === requestId.current) setLoading(false);
    }
  }, [ensureToken]);

  // Search after a pause in typing; a search in the URL on arrival runs straight away. Earlier results
  // stay on screen while the next ones load.
  useEffect(() => {
    if (!longEnough) {
      // Drop any reply still on its way for the old query
      requestId.current += 1;
      setUsers([]);
      setResultsFor(null);
      setError(null);
      setLoading(false);
      return undefined;
    }
    const delay = firstSearch.current ? 0 : 300;
    firstSearch.current = false;
    const timer = setTimeout(() => searchUsers(query), delay);
    return () => clearTimeout(timer);
  }, [query, longEnough, searchUsers]);

  const handleSearchChange = (e) => {
    const value = e.target.value;
    firstSearch.current = false;
    setSearchTerm(value);
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (value) next.set('q', value); else next.delete('q');
      return next;
    }, { replace: true });
  };

  const fullName = (user) => [user.firstname, user.lastname].filter(Boolean).join(' ');

  const renderResults = () => {
    if (error) {
      return <ErrorState message={error} onRetry={() => searchUsers(query)} inCard />;
    }
    if (!longEnough) {
      return <div className="p-4 text-center text-muted">Type at least 2 characters to search</div>;
    }
    if (resultsFor === null) {
      return <LoadingState label="Searching" />;
    }
    if (users.length === 0) {
      return <div className="p-4 text-center text-muted">No users found matching "{resultsFor}"</div>;
    }
    return (
      <>
        {users.length >= RESULT_LIMIT && (
          <div className="px-3 py-2 small text-muted border-bottom">
            Showing the first {RESULT_LIMIT} matches. Refine your search.
          </div>
        )}
        <StackedTable responsive className="mb-0">
          <thead className="table-light">
            <tr>
              <th>Name</th>
              <th>Email</th>
              <th>Company</th>
              <th className="text-center d-none d-xl-table-cell">Role</th>
            </tr>
          </thead>
          <tbody>
            {users.map((user) => (
              <tr
                key={user.id}
                onClick={rowClick(() => navigate(`/users/${user.id}`))}
                style={{ cursor: 'pointer' }}
                className="table-row-hover"
              >
                {/* Below desktop the role sits under the name, leaving room for email and company */}
                <td className="align-middle">
                  <Link className="cell-link" to={`/users/${user.id}`}>{fullName(user) || 'No name'}</Link>
                  {roleBadge(user.role) && <div className="mt-1 d-xl-none">{roleBadge(user.role)}</div>}
                </td>
                <td className="align-middle">{user.email || EMPTY}</td>
                <td className="align-middle">{user.companyName || EMPTY}</td>
                <td className="text-center align-middle d-none d-xl-table-cell">{roleBadge(user.role)}</td>
              </tr>
            ))}
          </tbody>
        </StackedTable>
      </>
    );
  };

  return (
    <Container fluid className="py-4">
      <Row className="mb-4">
        <Col>
          <h1>User search</h1>
          <p className="text-muted">Search users by name or email</p>
        </Col>
      </Row>

      <Card className="shadow-sm">
        <Card.Header className="bg-light">
          <Form.Group controlId="user-search">
            <Form.Label visuallyHidden>Search users</Form.Label>
            <InputGroup>
              <Form.Control
                type="search"
                placeholder="Search by name or email (min 2 characters)..."
                value={searchTerm}
                onChange={handleSearchChange}
                autoFocus
              />
              {loading && (
                <InputGroup.Text>
                  <Spinner animation="border" size="sm" role="status">
                    <span className="visually-hidden">Searching</span>
                  </Spinner>
                </InputGroup.Text>
              )}
            </InputGroup>
          </Form.Group>
        </Card.Header>
        <Card.Body className="p-0">
          {renderResults()}
        </Card.Body>
      </Card>
    </Container>
  );
};

export default UserSearch;
